import {
  ConfiguratorPV,
  getAttributeValue,
  ICfgCompositeVariant,
  IComponentProductKey,
  IComponentProductTextData,
  ICompositeProduct,
  IPrice,
} from '@canvas-logic/clp-domain';
import { createUrl } from './utils';
import FileSaver from 'file-saver';
import { Sdk } from '@/types';
import { AppConfig } from '@/config';
import { API } from '@canvas-logic/clp-viewer3d';
import QRCode from 'qrcode';
import {
  DEFAULT_LOCALIZATION_CONFIG,
  ILocalizationConfigDto,
  IPdfConfigDto,
  IPdfDto,
  IPdfSummary,
  IPdfSummaryItem,
} from '@canvas-logic/clp-api-types';
import i18next from '../../i18n';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';
import { resolveEffectiveLocale } from './localization-config/resolveEffectiveRegion';
import { getUrlLocale } from './localization-config/getUrlLocale';
import { formatSelectedPrice } from './price-selection';
import type { DisplayContext, TranslationLayers } from '@canvas-logic/clp-localization';
import { resolvePdfEntityLabel, resolvePdfUiLabel } from './pdfTranslations';

type TranslationObject = Record<string, string>;
const pdfDtoBase: Partial<IPdfDto> = {
  modelImage: '',
  logo: '',
  title: '',
  description: '',
  link: '',
  qrCode: '',
  totalPrice: '',
  summaryItems: {
    titleAttributes: [],
    items: [
      {
        name: '',
        description: '',
        article: '',
        amount: '',
        price: '',
      },
      {
        name: '',
        description: '',
        article: '',
        amount: '',
        price: '',
      },
    ],
  },
  address: '',
  email: '',
  phone: '',
  translations: {},
};

export class PdfService {
  private readonly QR_CODE_WIDTH = 200;
  private readonly PDF_API_URL = AppConfig.serverUrl + '/configurator/pdf/';
  private readonly TEMPLATE_URL = createUrl('assets/templates/pdf');
  private readonly t: (key: string) => string;
  private pdfConfig: IPdfConfigDto | null = null;
  private localizationConfig: ILocalizationConfigDto = DEFAULT_LOCALIZATION_CONFIG;
  private effectiveLocale: string = DEFAULT_LOCALIZATION_CONFIG.defaultRegion;
  private readonly selectedCurrency: string | undefined;
  private compositeName = '';
  private readonly catalogId: number;
  private readonly draft: boolean;
  // Layered project translations for the active language (ADR-0075), supplied
  // by the React layer at download time. Empty when unavailable; resolution
  // then falls back to i18next (UI chrome) / SDK texts (entity copy).
  private translations: TranslationLayers = {};

  constructor(
    private readonly sdk: Sdk,
    private readonly api3d: API,
    {
      catalogId,
      draft,
      selectedCurrency,
    }: {
      catalogId: number;
      draft: boolean;
      selectedCurrency?: string;
    },
  ) {
    this.catalogId = catalogId;
    this.draft = draft;
    this.selectedCurrency = selectedCurrency;
    this.t = (key: string) => i18next.t(key, { ns: 'pdf' });
  }

  public async downloadPdf(link: string, translations?: TranslationLayers): Promise<void> {
    try {
      this.translations = translations ?? {};
      await this.downloadPdfConfig();
      await this.downloadLocalizationConfig();
      const dto = await this.createPdfDto(link);
      const pdfBlob = await this.generatePdf(dto);
      await this.savePdfFile(pdfBlob);
    } catch (error) {
      console.error('Error during PDF generation:', error);
      throw error;
    }
  }

  private async createPdfDto(link: string): Promise<IPdfDto> {
    if (!this.sdk.composite) {
      throw new Error('No composite product found.');
    }
    if (!this.pdfConfig) {
      throw new Error(`No pdf config provided`);
    }

    const compositeApi = this.sdk.api;
    const [price, modelImage, _name, variants, _metaIds, product, productFullData] = await Promise.all([
      compositeApi.getPrice(),
      this.api3d.createScreenshot(2190 / 2, 2300 / 2),
      compositeApi.getName(),
      compositeApi.getVariant(),
      compositeApi.getMetaIds(),
      compositeApi.getProduct(),
      compositeApi.getProductData(),
    ]);
    const productData = compositeApi.getComponentsData();
    if (!productData) {
      throw new Error('No product data found.');
    }

    // Composite product scope (ADR-0045): resolve product copy and PDF chrome
    // through the project translation cascade, falling back to SDK texts /
    // i18next when the project has no translation for the active language.
    const cptKey = product.productType.productTypeKey;
    const cprodKey = product.productKey;
    // ADR-0076: the `pdf` display surface is applied by `resolvePdfEntityLabel`
    // itself, so these contexts stay pure scope. The cascade probes the whole
    // scope chain under `::pdf` first and then the same chain bare, so a project
    // that has authored no PDF copy renders exactly as before.
    const compositeCtx: DisplayContext = { cptKey, cprodKey };

    this.compositeName = resolvePdfEntityLabel(
      this.translations,
      `cpt:${cptKey}.name`,
      compositeCtx,
      productFullData.texts.name,
    );
    const qrCode = await this.generateQRCode(link, { width: this.QR_CODE_WIDTH });
    const totalPrice = this.formatTotalPrice(price);
    const summaryItems = this.transformDataToSummaryItems(compositeApi, variants, productData, cptKey);

    const titleAttributes = [];
    const attributeConfig = this.pdfConfig.titleAttributes.find(
      (it) => it.productTypeKey === product.productType.productTypeKey,
    );
    if (attributeConfig) {
      for (const it of attributeConfig.attributes) {
        const component = variants[it.componentKey];
        const name = it.name || `${it.componentKey}.${it.attributeKey}`;

        const translationKey = this.summaryAttributeMap[name];
        const translatedName = translationKey ? this.t(translationKey) : name;

        titleAttributes.push({
          name: translatedName,
          value: getAttributeValue(component[it.attributeKey]),
        });
      }
    }

    const pdfSummary: IPdfSummary = {
      titleAttributes,
      items: summaryItems,
    };

    const translations: TranslationObject = this.getPdfTranslations({
      idType: product.catalogMeta.metaId,
      cptKey,
    });

    return {
      ...pdfDtoBase,
      modelImage,
      qrCode,
      link,
      title: this.compositeName,
      description: this.pdfConfig.showCompositeProductDescription
        ? resolvePdfEntityLabel(
            this.translations,
            `cpt:${cptKey}.desc`,
            compositeCtx,
            productFullData.texts.description,
          )
        : '',
      shortDescription: this.pdfConfig.showCompositeProductShortDescription
        ? productFullData.texts.shortDescription
        : '',
      totalPrice: this.pdfConfig.showTotalPrice ? totalPrice : '',
      summaryItems: pdfSummary,
      logo: this.pdfConfig.logo ?? undefined,
      address: this.pdfConfig.contactInfo.address || '',
      phone: this.pdfConfig.contactInfo.phone || '',
      email: this.pdfConfig.contactInfo.email || '',
      translations,
      config: this.pdfConfig,
    };
  }

  private async generatePdf(pdfDto: IPdfDto): Promise<Blob> {
    const response = await fetch(this.PDF_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        templateUrl: this.TEMPLATE_URL,
        clearCache: true,
        model: pdfDto,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate PDF: ${response.statusText}`);
    }

    return response.blob();
  }

  private async savePdfFile(pdfBlob: Blob): Promise<void> {
    const fileName = this.generateFileName();
    FileSaver.saveAs(pdfBlob, fileName);
  }

  private transformDataToSummaryItems(
    compositeApi: ICompositeProductApi,
    variants: IPrice | ICfgCompositeVariant | ICompositeProduct,
    productData: Record<
      string,
      | (IComponentProductKey & {
          texts: IComponentProductTextData;
          images: { url: string }[];
        })
      | null
    >,
    cptKey?: string,
  ): IPdfSummaryItem[] {
    const items: IPdfSummaryItem[] = [];

    for (const [componentKey, variant] of Object.entries(variants)) {
      if (!compositeApi.isComponentInPdf(componentKey)) {
        continue;
      }
      const v = new ConfiguratorPV(variant);
      if (v.isNullish) {
        continue;
      }
      const data = productData[componentKey];

      const price =
        this.pdfConfig?.showBomItemPrices && !v.isNullish && v.meta
          ? formatSelectedPrice(v.meta.price, this.selectedCurrency, this.effectiveLocale)
          : '';

      const amount = compositeApi.getComponentPartsAmount(componentKey);
      if (amount > 0) {
        // Translate the BOM line's product name/description through the entity
        // cascade (ADR-0045). The component's product type key + product key
        // form the canonical (`<bpt|cpt>:<ptKey>.prod:<productKey>.{name|desc}`);
        // SDK `.texts` (default language) is the fallback.
        const pt = compositeApi.getComponentProductType(componentKey);
        const itemCtx: DisplayContext = { cptKey, cmpKey: componentKey };
        const itemName = data?.texts.name ?? '';
        const itemDesc = data?.texts.description ?? '';
        const resolvedName =
          pt && data?.productKey
            ? resolvePdfEntityLabel(
                this.translations,
                `${pt.isComposite ? 'cpt' : 'bpt'}:${pt.key}.prod:${data.productKey}.name`,
                itemCtx,
                itemName,
              )
            : itemName;
        const resolvedDesc =
          pt && data?.productKey
            ? resolvePdfEntityLabel(
                this.translations,
                `${pt.isComposite ? 'cpt' : 'bpt'}:${pt.key}.prod:${data.productKey}.desc`,
                itemCtx,
                itemDesc,
              )
            : itemDesc;

        items.push({
          view: '',
          image: this.pdfConfig?.showImage ? data?.images[0]?.url : undefined,
          name: resolvedName,
          description: this.pdfConfig?.showProductDescription ? resolvedDesc : '',
          shortDescription: this.pdfConfig?.showProductShortDescription ? data?.texts.shortDescription : '',
          article: v.isNullish ? '' : v?.meta.id,
          amount: amount.toString(),
          price,
        });
      }
    }
    return groupItems(items, getPdfSummaryItemKey);
  }

  private formatTotalPrice(price: IPrice): string {
    return formatSelectedPrice(price, this.selectedCurrency, this.effectiveLocale);
  }

  private generateFileName(): string {
    const now = new Date();
    const dateString = new Intl.DateTimeFormat('en-GB').format(now).replace(/\//g, '.');
    const timeString = `${now.getHours()}_${now.getMinutes()}_${now.getSeconds()}`;
    return `${this.compositeName}_${dateString}_${timeString}.pdf`;
  }

  private async generateQRCode(link: string, options: Record<string, unknown>): Promise<string> {
    try {
      return await QRCode.toDataURL(link, options);
    } catch (error) {
      console.error('QR code generation error:', error);
      throw error;
    }
  }

  private async downloadPdfConfig() {
    if (!this.pdfConfig) {
      const url = `${AppConfig.serverUrl}/configurator/pdf-config?catalogId=${this.catalogId}&version=${
        this.draft ? 'draft' : 'active'
      }`;
      const response = await fetch(url);
      this.pdfConfig = await response.json();
      this.normalizePdfConfig();
    }
  }

  private async downloadLocalizationConfig() {
    const url = `${AppConfig.serverUrl}/configurator/localization-config?catalogId=${this.catalogId}&version=${
      this.draft ? 'draft' : 'active'
    }`;
    const response = await fetch(url);
    if (response.ok) {
      this.localizationConfig = await response.json();
    }
    this.effectiveLocale = resolveEffectiveLocale({
      urlLocale: getUrlLocale(),
      catalogDefaultRegion: this.localizationConfig.defaultRegion,
    });
  }

  /**
   * Backward-compat: if old `showPrices` exists and new fields don't, map old to new.
   */
  private normalizePdfConfig() {
    if (!this.pdfConfig) return;
    const config = this.pdfConfig as IPdfConfigDto & { showPrices?: boolean };
    if (
      config.showPrices !== undefined &&
      config.showTotalPrice === undefined &&
      config.showBomItemPrices === undefined
    ) {
      config.showTotalPrice = config.showPrices;
      config.showBomItemPrices = config.showPrices;
    }
    if (config.showBomTable === undefined) {
      config.showBomTable = true;
    }
    delete config.showPrices;
  }

  private getPdfTranslations({ idType, cptKey }: Partial<{ idType: string; cptKey: string }> = {}) {
    // Each PDF chrome slot resolves from its `ui.pdf.*` canonical (ADR-0045),
    // falling back to the bundled i18next `pdf.json` value when the project has
    // no translation for the active language.
    const ui = (canonical: string, fallbackKey: string) =>
      resolvePdfUiLabel(this.translations, canonical, this.t(fallbackKey), cptKey);
    return {
      price_disclaimer: ui('ui.pdf.priceDisclaimer', 'first_page.price_disclaimer'),
      link_info_text: ui('ui.pdf.linkInfo.label', 'first_page.link_info.your_configuration_text'),
      link_info_link: ui('ui.pdf.linkInfo.link', 'first_page.link_info.your_configuration_link'),
      link_info_qr: ui('ui.pdf.linkInfo.orScanQr', 'first_page.link_info.or_scan_qr'),
      summary_title: ui('ui.pdf.summaryTitle', 'summary_page.title'),
      // No project canonical exists for the per-attribute "Color" header; it is
      // mapped from attribute names elsewhere, so keep the i18next value.
      summary_color_attribute: this.t('summary_page.attributes.color'),
      summary_columns_product: ui('ui.pdf.column.product', 'summary_page.columns.product'),
      summary_columns_article: idType ?? ui('ui.pdf.column.article', 'summary_page.columns.article'),
      summary_columns_amount: ui('ui.pdf.column.amount', 'summary_page.columns.amount'),
      summary_columns_price: ui('ui.pdf.column.price', 'summary_page.columns.price'),
      footer_contact_us: ui('ui.pdf.footer.contactUs', 'summary_page.footer.contact_us'),
    };
  }

  private summaryAttributeMap: Record<string, string> = {
    Color: 'summary_page.attributes.color',
  };

  private groupItems(_items: IPdfSummaryItem[]): IPdfSummaryItem[] {
    return [];
  }
}

export function getPdfSummaryItemKey(item: IPdfSummaryItem): string {
  const result: string[] = [];
  for (const [key, value] of Object.entries(item)) {
    if (key === 'amount') {
      continue;
    }
    result.push(value?.toString() ?? '');
  }
  return result.join(':=:');
}
export function groupItems(items: IPdfSummaryItem[], keyFn: (item: IPdfSummaryItem) => string): IPdfSummaryItem[] {
  const groups = new Map<string, IPdfSummaryItem>();
  for (const item of items) {
    const key = keyFn(item);
    const groupedItem = groups.get(key);
    if (!groupedItem) {
      groups.set(key, item);
    } else {
      groupedItem.amount = (+groupedItem.amount + +item.amount).toString();
    }
  }

  const result = Array.from(groups.values());
  return result;
}
